import { create } from "zustand";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";
import {
    DefaultAppActivity,
    ModuleType,
    OnboardingAppData,
    OnboardingQnAData,
} from "./OnboardingPortal.types";

interface OnboardingPortalState {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    showModule: ModuleType;
    setShowModule: (m: ModuleType) => void;
    defaultApps: OnboardingAppData[];
    defaultAppActivity: DefaultAppActivity[];
    onboardedApps: OnboardingAppData[];
    onboardedQnA: OnboardingQnAData[];
    reviewApps: OnboardingAppData[];
    reviewQnA: OnboardingQnAData[];
    reviewAllApps: OnboardingAppData[];
    reviewDefaultAppActivity: DefaultAppActivity[];
    reviewAllQnA: OnboardingQnAData[];
    isLoadingDefaultApps: boolean;
    isLoadingDefaultAppActivity: boolean;
    isLoadingOnboardedApps: boolean;
    isLoadingOnboardedQnA: boolean;
    isLoadingReviewApps: boolean;
    isLoadingReviewQnA: boolean;
    isLoadingReviewDefaultAppActivity: boolean;
    isSubmittingReviewApp: boolean;
    isSubmittingReviewDefaultAppActivity: boolean;
    isSubmittingReviewQnA: boolean;
    fetchDefaultApps: (jwt: JwtTokenFn) => Promise<void>;
    fetchDefaultAppActivity: (jwt: JwtTokenFn) => Promise<void>;
    fetchOnboardedApps: (jwt: JwtTokenFn) => Promise<void>;
    fetchOnboardedQnA: (jwt: JwtTokenFn) => Promise<void>;
    fetchReviewApps: (jwt: JwtTokenFn) => Promise<void>;
    fetchReviewQnA: (jwt: JwtTokenFn) => Promise<void>;
    fetchReviewDefaultAppActivity: (jwt: JwtTokenFn) => Promise<void>;
    createNewQnA: number;
    setCreateNewQnA: () => void;
    showEditApp: boolean;
    editAppData: OnboardingAppData | null;
    onEditApp: (appData: OnboardingAppData) => void;
    resetEditApp: () => void;
    showEditQnA: boolean;
    editQnAData: OnboardingQnAData | null;
    onEditQnA: (qnaData: OnboardingQnAData) => void;
    resetEditQnA: () => void;
    showViewApp: boolean;
    viewAppData: OnboardingAppData | null;
    onViewApp: (appData: OnboardingAppData) => void;
    resetViewApp: () => void;
    showViewQnA: boolean;
    viewQnAData: OnboardingQnAData | null;
    onViewQnA: (qnaData: OnboardingQnAData) => void;
    resetViewQnA: () => void;
    showReviewApp: boolean;
    reviewAppData: OnboardingAppData | null;
    onReviewApp: (appData: OnboardingAppData) => void;
    resetReviewApp: () => void;
    handleReviewApp: (
        jwt: JwtTokenFn,
        id: string,
        action: "approved" | "rejected" | "inQA" | "inProd",
        notes: string
    ) => Promise<void>;
    handleReviewDefaultAppActivity: (
        jwt: JwtTokenFn,
        updates: {
            entityId: string;
            reviewerAction: "approved" | "rejected" | "inQA" | "inProd";
        }[]
    ) => Promise<void>;
    showReviewQnA: boolean;
    reviewQnAData: OnboardingQnAData | null;
    onReviewQnA: (qnaData: OnboardingQnAData) => void;
    resetReviewQnA: () => void;
    handleReviewQnA: (
        jwt: JwtTokenFn,
        id: string,
        action: "approved" | "rejected" | "inQA" | "inProd",
        notes: string
    ) => Promise<void>;
}

const API_BASE_URL = EnvironmentConfig.nodeService;
const API_ENDPOINT_APPS = `${API_BASE_URL}/service/onboarding/apps`;
const API_ENDPOINT_QNA = `${API_BASE_URL}/service/onboarding/qna`;

const initialState: Omit<
    OnboardingPortalState,
    | "setLoading"
    | "setShowModule"
    | "fetchDefaultApps"
    | "fetchDefaultAppActivity"
    | "fetchOnboardedApps"
    | "fetchOnboardedQnA"
    | "fetchReviewApps"
    | "fetchReviewQnA"
    | "fetchReviewDefaultAppActivity"
    | "onEditApp"
    | "onEditQnA"
    | "resetEditApp"
    | "resetEditQnA"
    | "onViewApp"
    | "resetViewApp"
    | "onViewQnA"
    | "resetViewQnA"
    | "onReviewApp"
    | "resetReviewApp"
    | "onReviewQnA"
    | "resetReviewQnA"
    | "setCreateNewQnA"
    | "handleReviewApp"
    | "handleReviewDefaultAppActivity"
    | "handleReviewQnA"
> = {
    loading: false,
    showModule: "home",
    defaultApps: [],
    defaultAppActivity: [],
    onboardedApps: [],
    onboardedQnA: [],
    reviewApps: [],
    reviewQnA: [],
    reviewAllApps: [],
    reviewAllQnA: [],
    showEditApp: false,
    editAppData: null,
    showEditQnA: false,
    editQnAData: null,
    showViewApp: false,
    viewAppData: null,
    showViewQnA: false,
    viewQnAData: null,
    showReviewApp: false,
    reviewAppData: null,
    showReviewQnA: false,
    reviewQnAData: null,
    reviewDefaultAppActivity: [],
    createNewQnA: 0,
    isLoadingDefaultApps: false,
    isLoadingDefaultAppActivity: false,
    isLoadingOnboardedApps: false,
    isLoadingOnboardedQnA: false,
    isLoadingReviewApps: false,
    isLoadingReviewQnA: false,
    isLoadingReviewDefaultAppActivity: false,
    isSubmittingReviewApp: false,
    isSubmittingReviewDefaultAppActivity: false,
    isSubmittingReviewQnA: false,
};

const useOnboardingPortalStore = create<OnboardingPortalState>(
    (set, getState) => ({
        ...initialState,
        setShowModule: (m: ModuleType) => set({ showModule: m }),
        setLoading: (loading: boolean) => set({ loading }),
        setCreateNewQnA: () =>
            set((state) => ({ createNewQnA: state.createNewQnA + 1 })),
        fetchDefaultApps: async (jwt: JwtTokenFn) => {
            set({ isLoadingDefaultApps: true });
            try {
                const token = await jwt();
                const response = await fetch(
                    `${API_ENDPOINT_APPS}?view=default-apps`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean; apps: OnboardingAppData[] } =
                    await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                set({ defaultApps: data.apps });
            } catch (error) {
                console.error("Error in fetchDefaultApps:", error);
                throw error;
            } finally {
                set({ isLoadingDefaultApps: false });
            }
        },
        fetchDefaultAppActivity: async (jwt: JwtTokenFn) => {
            set({ isLoadingDefaultAppActivity: true });
            try {
                const token = await jwt();
                const response = await fetch(
                    `${API_ENDPOINT_APPS}?view=default-app-activity`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean; apps: DefaultAppActivity[] } =
                    await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                set({ defaultAppActivity: data.apps });
            } catch (error) {
                console.error("Error in fetchDefaultAppActivity:", error);
                throw error;
            } finally {
                set({ isLoadingDefaultAppActivity: false });
            }
        },
        fetchOnboardedApps: async (jwt: JwtTokenFn) => {
            set({ isLoadingOnboardedApps: true });
            try {
                const token = await jwt();
                const response = await fetch(API_ENDPOINT_APPS, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean; apps: OnboardingAppData[] } =
                    await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                set({ onboardedApps: data.apps });
            } catch (error) {
                console.error("Error in fetchOnboardedApps:", error);
                throw error;
            } finally {
                set({ isLoadingOnboardedApps: false });
            }
        },
        fetchOnboardedQnA: async (jwt: JwtTokenFn) => {
            set({ isLoadingOnboardedQnA: true });
            try {
                const token = await jwt();
                const response = await fetch(API_ENDPOINT_QNA, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean; qna: OnboardingQnAData[] } =
                    await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                set({ onboardedQnA: data.qna });
            } catch (error) {
                console.error("Error in fetchOnboardedQnA:", error);
                throw error;
            } finally {
                set({ isLoadingOnboardedQnA: false });
            }
        },
        fetchReviewApps: async (jwt: JwtTokenFn) => {
            set({ isLoadingReviewApps: true });
            try {
                const token = await jwt();
                const response = await fetch(
                    `${API_ENDPOINT_APPS}?view=review-apps`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean; apps: OnboardingAppData[] } =
                    await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                const filteredApps = data.apps.filter(
                    (a) => a.status === "pending"
                );
                set({ reviewApps: filteredApps, reviewAllApps: data.apps });
            } catch (error) {
                console.error("Error in fetchReviewApps:", error);
                throw error;
            } finally {
                set({ isLoadingReviewApps: false });
            }
        },
        fetchReviewQnA: async (jwt: JwtTokenFn) => {
            set({ isLoadingReviewQnA: true });
            try {
                const token = await jwt();
                const response = await fetch(
                    `${API_ENDPOINT_QNA}?view=review-qna`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean; qna: OnboardingQnAData[] } =
                    await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                const filteredQnA = data.qna.filter(
                    (q) => q.status === "pending"
                );
                set({ reviewQnA: filteredQnA, reviewAllQnA: data.qna });
            } catch (error) {
                console.error("Error in fetchReviewQnA:", error);
                throw error;
            } finally {
                set({ isLoadingReviewQnA: false });
            }
        },
        fetchReviewDefaultAppActivity: async (jwt: JwtTokenFn) => {
            set({ isLoadingReviewDefaultAppActivity: true });
            try {
                const token = await jwt();
                const response = await fetch(
                    `${API_ENDPOINT_APPS}?view=review-default-app-activity`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean; apps: DefaultAppActivity[] } =
                    await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                set({ reviewDefaultAppActivity: data.apps });
            } catch (error) {
                console.error("Error in fetchReviewDefaultAppActivity:", error);
                throw error;
            } finally {
                set({ isLoadingReviewDefaultAppActivity: false });
            }
        },
        onEditApp: (appData: OnboardingAppData) =>
            set({
                showEditApp: true,
                editAppData: appData,
                showModule: "edit-apps",
            }),
        onEditQnA: (qnaData: OnboardingQnAData) =>
            set({
                showEditQnA: true,
                editQnAData: qnaData,
                showModule: "edit-qna",
            }),
        resetEditApp: () =>
            set({
                showEditApp: false,
                editAppData: null,
                showModule: "manage-apps",
            }),
        resetEditQnA: () =>
            set({
                showEditQnA: false,
                editQnAData: null,
                showModule: "manage-qna",
            }),
        onViewApp: (appData: OnboardingAppData) =>
            set({
                showViewApp: true,
                viewAppData: appData,
            }),
        resetViewApp: () =>
            set({
                showViewApp: false,
                viewAppData: null,
            }),
        onViewQnA: (qnaData: OnboardingQnAData) =>
            set({
                showViewQnA: true,
                viewQnAData: qnaData,
            }),
        resetViewQnA: () =>
            set({
                showViewQnA: false,
                viewQnAData: null,
            }),
        onReviewApp: (appData: OnboardingAppData) =>
            set({
                showReviewApp: true,
                reviewAppData: appData,
            }),
        resetReviewApp: () =>
            set({
                showReviewApp: false,
                reviewAppData: null,
            }),
        onReviewQnA: (qnaData: OnboardingQnAData) =>
            set({
                showReviewQnA: true,
                reviewQnAData: qnaData,
            }),
        resetReviewQnA: () =>
            set({
                showReviewQnA: false,
                reviewQnAData: null,
            }),
        handleReviewApp: async (
            jwt: JwtTokenFn,
            id: string,
            action: "approved" | "rejected" | "inQA" | "inProd",
            notes: string
        ) => {
            set({ isSubmittingReviewApp: true });
            try {
                const token = await jwt();
                const response = await fetch(`${API_ENDPOINT_APPS}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        updateType: "review",
                        entityId: id,
                        reviewerNote: notes || "",
                        reviewerAction: action,
                    }),
                });
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean } = await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                alert(`App Reviewed successfully!`);
                if (action === "approved" || action === "rejected") {
                    getState().resetReviewApp();
                }
                await getState().fetchReviewApps(jwt);
            } catch (error) {
                console.error("Error in handleReviewApp:", error);
                throw error;
            } finally {
                set({ isSubmittingReviewApp: false });
            }
        },
        handleReviewDefaultAppActivity: async (
            jwt: JwtTokenFn,
            updates: {
                entityId: string;
                reviewerAction: "approved" | "rejected" | "inQA" | "inProd";
            }[]
        ) => {
            set({ isSubmittingReviewDefaultAppActivity: true });
            try {
                const token = await jwt();
                const response = await fetch(`${API_ENDPOINT_APPS}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        updateType: "review-default-app-activity",
                        updates,
                    }),
                });
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean } = await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                alert(`App Reviewed successfully!`);
                await getState().fetchReviewDefaultAppActivity(jwt);
            } catch (error) {
                console.error(
                    "Error in handleReviewDefaultAppActivity:",
                    error
                );
                throw error;
            } finally {
                set({ isSubmittingReviewDefaultAppActivity: false });
            }
        },
        handleReviewQnA: async (
            jwt: JwtTokenFn,
            id: string,
            action: "approved" | "rejected" | "inQA" | "inProd",
            notes: string
        ) => {
            set({ isSubmittingReviewQnA: true });
            try {
                const token = await jwt();
                const response = await fetch(`${API_ENDPOINT_QNA}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        updateType: "review",
                        entityId: id,
                        reviewerNote: notes || "",
                        reviewerAction: action,
                    }),
                });
                if (!response.ok) {
                    const errorBody = await response.json();
                    throw new Error(
                        `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                    );
                }
                const data: { isSuccess: boolean } = await response.json();
                if (data.isSuccess !== true) {
                    console.warn(
                        "API call succeeded, but response not marked as success."
                    );
                }
                alert(`QnA Reviewed successfully!`);
                if (action === "approved" || action === "rejected") {
                    getState().resetReviewQnA();
                }
                await getState().fetchReviewQnA(jwt);
            } catch (error) {
                console.error("Error in handleReviewQnA:", error);
                throw error;
            } finally {
                set({ isSubmittingReviewQnA: false });
            }
        },
    })
);

export default useOnboardingPortalStore;
